import { useEffect, useState, type FormEvent } from "react";
import { API, api, ApiError } from "../api";
import { Mascot } from "../components/Mascot";

const SIGNIN_ERRORS: Record<string, string> = {
  not_staff: "Microsoft sign-in is for Departure Labs staff. Customers: sign in with your email below.",
  wrong_tenant: "That Microsoft account isn't in the Departure Labs organization.",
  cancelled: "Microsoft sign-in was cancelled.",
  expired: "That sign-in took too long or was already used. Try again.",
  no_email: "Your Microsoft account didn't share an email address. Ask an admin to check your account.",
  no_access: "This account doesn't have portal access. Contact support@departurelabs.io.",
  microsoft_unavailable: "Microsoft sign-in isn't set up here yet.",
  misconfigured: "Microsoft sign-in is misconfigured on our side (the app's client secret). Tell a Departure Labs admin, or use an email code.",
  bad_token: "Microsoft's sign-in response couldn't be verified. Try again.",
};

/** Reads (and clears) ?signin_error=… left by the Microsoft callback. */
function useSigninError(): string | null {
  const [msg] = useState(() => {
    const code = new URLSearchParams(window.location.search).get("signin_error");
    if (!code) return null;
    window.history.replaceState(null, "", window.location.pathname);
    return SIGNIN_ERRORS[code] ?? "Microsoft sign-in didn't work. Try again, or use an email code.";
  });
  return msg;
}

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const signinError = useSigninError();
  const [microsoft, setMicrosoft] = useState(false);
  useEffect(() => {
    api<{ microsoft: boolean }>("/auth/methods")
      .then((m) => setMicrosoft(m.microsoft))
      .catch(() => null);
  }, []);
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/auth/request", { method: "POST", json: { email } });
      setStep("code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/auth/verify", { method: "POST", json: { email, code } });
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
      setBusy(false);
    }
  };

  return (
    <main className="login-wrap">
      <div className="login">
        <Mascot wave className="mascot-wave" />
        <div className="login-card">
          <p className="eyebrow">Departure Labs · Support</p>
          {signinError && (
            <p className="notice notice-danger" role="alert" style={{ marginTop: 8 }}>
              {signinError}
            </p>
          )}
          {step === "email" ? (
            <form onSubmit={requestCode} className="stack" style={{ marginTop: 8 }}>
              <h1>Sign in</h1>
              {microsoft && (
                <>
                  <a className="btn" href={`${API}/auth/microsoft/start`} data-tour="microsoft-signin">
                    <MicrosoftMark /> Departure Labs staff: Sign in with Microsoft
                  </a>
                  <div className="divider-text small muted" aria-hidden="true">
                    or use an email code
                  </div>
                </>
              )}
              <p className="muted" style={{ margin: 0 }}>
                We'll email you a one-time code. No password needed.
              </p>
              <div className="field">
                <label htmlFor="email">Work email</label>
                <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
              </div>
              {error && <p className="error-text" role="alert">{error}</p>}
              <button className="btn btn-primary" disabled={busy}>
                {busy ? "Sending…" : "Email me a code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="stack" style={{ marginTop: 8 }}>
              <h1>Check your email</h1>
              <p className="muted" style={{ margin: 0 }}>
                If <strong>{email}</strong> has access, a 6-digit code is on its way. It expires in 10 minutes.
              </p>
              <div className="field">
                <label htmlFor="code">Code</label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  className="mono"
                  style={{ fontSize: 22, letterSpacing: 6 }}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  autoFocus
                />
              </div>
              {error && <p className="error-text" role="alert">{error}</p>}
              <button className="btn btn-primary" disabled={busy || code.length !== 6}>
                {busy ? "Checking…" : "Sign in"}
              </button>
              <button type="button" className="btn-link" onClick={() => (setStep("email"), setCode(""), setError(null))}>
                Use a different email
              </button>
            </form>
          )}
          <p className="small muted" style={{ marginTop: 20, marginBottom: 0 }}>
            No access? Email <a href="mailto:support@departurelabs.io">support@departurelabs.io</a>.
          </p>
        </div>
      </div>
    </main>
  );
}

/** Microsoft's four-square logo (brand colors are part of the mark, per Microsoft's sign-in button guidelines). */
function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
